<script setup lang="ts">
/**
 * GallerySection —— `.scu-widget` 壳 + 图片网格（2026-06-25 R1/R6）。
 *
 * 模板无一一对应区，按 issue 复用 `.scu-widget` + `.scu-grid-item` 视觉
 * 语言：SectionCard 提供 widget 壳，每张图片渲染为 scu-grid-item 风格
 * 卡片（bg / border / hover 上移），`<img loading="lazy">`。
 */
import { computed } from 'vue';

import SectionCard from './SectionCard.vue';
import FallbackImage from '@/components/FallbackImage.vue';
import type { SectionComponentProps } from './section-props';
import { copy } from '@/i18n/zh-CN';
import { hasText, sectionImages } from './section-presenters';

const props = defineProps<SectionComponentProps>();
const images = computed(() => sectionImages(props.section));
</script>

<template>
  <SectionCard
    :id="props.id"
    type="gallery"
    :title="props.section.title"
    :summary="props.section.summary"
    :show-draft-badge="props.showDraftBadge"
  >
    <div v-if="images.length > 0" :class="$style.grid">
      <figure
        v-for="(image, index) in images"
        :key="`${image.url}-${index}`"
        :class="$style.item"
      >
        <FallbackImage
          :src="image.url"
          :alt="hasText(image.caption) ? image.caption : props.section.title"
          :context="`gallery section='${props.section.id}' image#${index}`"
        />
        <figcaption v-if="hasText(image.caption)" :class="$style.caption">
          {{ image.caption }}
        </figcaption>
      </figure>
    </div>
    <p v-else :class="$style.empty">{{ copy.sections.empty }}</p>
  </SectionCard>
</template>

<style module>
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(var(--section-image-card-min), 1fr));
  gap: var(--space-2);
}

.item {
  margin: 0;
  overflow: hidden;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  transition:
    box-shadow var(--template-hover-duration) ease,
    transform var(--template-hover-duration) ease,
    border-color var(--template-hover-duration) ease;
}

.item:hover {
  border-color: var(--color-brand-primary);
  box-shadow: var(--shadow-card);
  transform: translateY(var(--template-hover-lift));
}

.item img {
  width: 100%;
  display: block;
  object-fit: cover;
}

.caption {
  padding: var(--space-1) var(--space-2);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.empty {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
}
</style>
